import React, { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowRight, Check, Send } from "lucide-react";
import { SERVICES } from "../mock";
import { PageHero, Eyebrow, Icon, CtaBanner, Reveal } from "../components/shared";
import { SimpleServiceGrid } from "../components/ServiceCards";
import { useToast } from "../hooks/use-toast";
import { submitQuote } from "../api";

function QuoteBox({ serviceTitle }) {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast({ title: "Missing details", description: "Please add your name and email." });
      return;
    }
    setSubmitting(true);
    try {
      await submitQuote({ ...form, service: serviceTitle, source: "service" });
      toast({ title: "Request sent", description: "Thanks! We'll be in touch shortly to prepare your quote." });
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      toast({ title: "Something went wrong", description: "Please try again or email us directly." });
    } finally {
      setSubmitting(false);
    }
  };

  const box = "w-full rounded-lg bg-white border border-white px-4 py-3 text-[14px] text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-white/60 transition-all";

  return (
    <form onSubmit={handleSubmit} className="h-full rounded-2xl bg-[#0b2545] p-7 lg:p-8 shadow-sm flex flex-col">
      <p className="text-[11px] font-semibold tracking-[0.22em] uppercase text-white/70">Free Consultation</p>
      <h3 className="mt-1 font-display font-extrabold text-white text-[22px]">Request a Quotation</h3>
      <p className="mt-2 text-[14px] text-white/80">For {serviceTitle}. No obligation.</p>
      <div className="mt-6 space-y-3">
        <input data-testid="service-quote-name-input" className={box} placeholder="Your name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        <input type="email" data-testid="service-quote-email-input" className={box} placeholder="Email address" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
        <textarea rows={3} data-testid="service-quote-message-input" className={`${box} resize-none`} placeholder="Brief project details..." value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} />
      </div>
      <button type="submit" data-testid="service-quote-submit-button" disabled={submitting} className="mt-4 w-full inline-flex items-center justify-center gap-2 rounded-lg bg-[#1e73ff] text-white text-[15px] font-semibold py-3 hover:bg-[#155ed6] transition-colors disabled:opacity-60">
        {submitting ? "Sending..." : "Send Request"} <Send className="w-4 h-4" />
      </button>
    </form>
  );
}

export default function ServiceDetailPage() {
  const { slug } = useParams();
  const service = SERVICES.find((s) => s.slug === slug);

  if (!service) {
    return (
      <section className="py-32 text-center bg-[#eaf2ff]">
        <h1 className="font-display font-extrabold text-4xl text-[#0b2545]">Service not found</h1>
        <Link to="/services" className="mt-6 inline-flex items-center gap-2 text-[#1e73ff] font-semibold">
          Back to services <ArrowRight className="w-4 h-4" />
        </Link>
      </section>
    );
  }

  return (
    <>
      <PageHero crumb={service.title} eyebrow="Our Services" title={service.title} variant="brand" />

      <section className="py-10 lg:py-14 bg-[#eaf2ff]">
        <div className="max-w-[1240px] mx-auto px-5 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-6 lg:gap-8 items-stretch">
            {/* A — Overview */}
            <Reveal className="bg-white rounded-2xl border border-black/5 shadow-sm p-8">
              <div className="w-12 h-12 rounded-full bg-[#eaf2ff] flex items-center justify-center">
                <Icon name={service.icon} className="w-6 h-6 text-[#1e73ff]" />
              </div>
              <h2 className="mt-5 font-display font-extrabold text-[#0b2545] text-[26px] hero-heading">{service.title}</h2>
              <p className="mt-3 text-[16px] text-slate-600 leading-relaxed">{service.short}</p>
            </Reveal>

            {/* B — What we deliver */}
            <Reveal delay={100} className="bg-white rounded-2xl border border-black/5 shadow-sm p-8">
              <h3 className="font-display font-bold text-[22px] text-[#0b2545]">What we deliver</h3>
              <p className="mt-3 text-[15px] text-slate-600 leading-relaxed">{service.deliver}</p>
              <ul className="mt-5 space-y-2.5">
                {service.bullets.map((b, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-[14px] text-[#0b2545]">
                    <Check className="w-4 h-4 text-[#1e73ff] mt-0.5 shrink-0" strokeWidth={3} />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            </Reveal>

            {/* C — Image */}
            <Reveal delay={80} className="overflow-hidden rounded-2xl shadow-sm min-h-[300px]">
              <img src={service.image} alt={service.title} className="w-full h-full min-h-[300px] object-cover" />
            </Reveal>

            {/* D — Purple quotation box */}
            <Reveal delay={160}>
              <QuoteBox serviceTitle={service.title} />
            </Reveal>
          </div>
        </div>
      </section>

      <section className="py-20 lg:py-24 bg-white">
        <div className="max-w-[1240px] mx-auto px-5 lg:px-8">
          <Eyebrow className="mb-5">Explore More</Eyebrow>
          <h2 className="font-display font-extrabold text-[#0b2545] text-[32px] lg:text-[44px] hero-heading mb-12">
            Other Services
          </h2>
          <SimpleServiceGrid />
        </div>
      </section>

      <CtaBanner />
    </>
  );
}
